import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Download, Search, Trash2, Inbox, Phone, Mail, Calendar, Tag,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IMAGES } from "@/data/content";

const STATUSES = ["Nouveau", "Contacté", "Client"];
const STATUS_STYLE = {
  Nouveau: "bg-gold/15 text-golddark border-gold/30",
  "Contacté": "bg-sage/15 text-sage border-sage/30",
  Client: "bg-forest/10 text-forest border-forest/20",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0 });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filter) params.status = filter;
      const [l, s] = await Promise.all([
        api.get("/admin/leads", { params }),
        api.get("/admin/leads/stats"),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch {
      toast.error("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  }, [search, filter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      api.get("/admin/leads/stats").then((s) => setStats(s.data));
      toast.success("Statut mis à jour.");
    } catch {
      toast.error("Mise à jour impossible.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      api.get("/admin/leads/stats").then((s) => setStats(s.data));
      toast.success("Demande supprimée.");
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/leads/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("proxilia_token")}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "proxilia_demandes.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé.");
    } catch {
      toast.error("Export impossible.");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-64 bg-forest text-cream md:min-h-screen p-6 flex md:flex-col justify-between md:sticky md:top-0">
        <div>
          <img src={IMAGES.logoLight} alt="Proxilia" className="h-7 w-auto object-contain" />
          <p className="text-xs text-cream/50 mt-1 hidden md:block">Back-office</p>
          <nav className="hidden md:block mt-10 space-y-1">
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-cream/10 text-cream font-medium text-sm">
              <Inbox className="w-4 h-4" /> Demandes
            </div>
          </nav>
        </div>
        <div className="flex items-center gap-3 md:flex-col md:items-stretch">
          <div className="hidden md:block text-xs text-cream/50 px-4">{user?.email}</div>
          <button onClick={doLogout} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-cream/80 hover:bg-cream/10 transition-colors text-sm" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl text-forest">Demandes de rappel</h1>
            <p className="text-forest/55 text-sm mt-1">Gérez et suivez vos prospects</p>
          </div>
          <button onClick={exportCsv} className="flex items-center gap-2 bg-gold text-white px-5 py-3 rounded-full font-medium hover:bg-golddark transition-all active:scale-95" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total", value: stats.total ?? 0, key: "" },
            { label: "Nouveau", value: stats.Nouveau ?? 0, key: "Nouveau" },
            { label: "Contacté", value: stats["Contacté"] ?? 0, key: "Contacté" },
            { label: "Client", value: stats.Client ?? 0, key: "Client" },
          ].map((s) => (
            <button
              key={s.label}
              onClick={() => setFilter(s.key)}
              className={`text-left bg-white rounded-2xl p-5 border transition-all hover:shadow-md ${filter === s.key ? "border-gold ring-1 ring-gold/30" : "border-forest/5"}`}
              data-testid={`stat-${s.label.toLowerCase()}`}
            >
              <div className="font-display text-3xl text-forest">{s.value}</div>
              <div className="text-xs text-forest/55 mt-1">{s.label}</div>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
          <input
            className="w-full bg-white border border-forest/8 pl-11 pr-4 py-3 rounded-xl text-forest placeholder:text-forest/40 focus:outline-none focus:border-gold transition-colors"
            placeholder="Rechercher par nom, email, téléphone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-input"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-forest/50 font-cormorant text-2xl animate-pulse">Chargement…</div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <Inbox className="w-12 h-12 text-forest/20 mx-auto" strokeWidth={1.5} />
            <p className="mt-4 text-forest/50">Aucune demande pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-list">
            {leads.map((l) => (
              <div key={l.id} className="bg-white rounded-2xl border border-forest/5 p-5 md:p-6 flex flex-col lg:flex-row lg:items-center gap-4 hover:shadow-md transition-all" data-testid={`lead-row-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-medium text-forest text-lg">{l.name}</span>
                    {l.plan && (
                      <span className="inline-flex items-center gap-1 text-xs bg-cream px-2.5 py-1 rounded-full text-forest/70">
                        <Tag className="w-3 h-3" /> {l.plan}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-2 text-sm text-forest/60">
                    <a href={`tel:${l.phone}`} className="flex items-center gap-1.5 hover:text-forest"><Phone className="w-3.5 h-3.5" /> {l.phone}</a>
                    <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-forest"><Mail className="w-3.5 h-3.5" /> {l.email}</a>
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmtDate(l.created_at)}</span>
                  </div>
                  {l.message && <p className="mt-2.5 text-sm text-forest/70 bg-cream/60 rounded-lg px-3 py-2">{l.message}</p>}
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <select
                    value={l.status}
                    onChange={(e) => updateStatus(l.id, e.target.value)}
                    className={`text-sm font-medium border rounded-full px-3 py-2 focus:outline-none cursor-pointer ${STATUS_STYLE[l.status]}`}
                    data-testid={`status-select-${l.id}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(l.id)} className="w-9 h-9 rounded-full flex items-center justify-center text-forest/40 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-${l.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
